/**
 * ClaimTableHeader Component
 *
 * Responsive header row for the billing claims table.
 * Adapts columns based on the layout mode determined by screen width.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { TableLayoutMode } from './types';
import { getGridTemplateColumns, GRID_GAP } from './types';

interface ClaimTableHeaderProps {
  layout: TableLayoutMode;
}

export const ClaimTableHeader: React.FC<ClaimTableHeaderProps> = ({ layout }) => {
  const theme = useTheme();

  if (layout === 'card') return null;

  const headerStyle = {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };

  const showProvider = layout === 'full' || layout === 'standard';
  const showPayer = layout === 'full';
  const showDate = layout === 'full' || layout === 'standard' || layout === 'compact';
  const showIssues = layout === 'full' || layout === 'standard';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: getGridTemplateColumns(layout),
        gap: GRID_GAP,
        alignItems: 'center',
        px: 2,
        py: 1,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      {/* Date — always visible (matches encounters order) */}
      {showDate && (
        <Typography variant="caption" sx={headerStyle}>
          Date
        </Typography>
      )}

      {showProvider && (
        <Typography variant="caption" sx={headerStyle}>
          Provider
        </Typography>
      )}

      <Typography variant="caption" sx={headerStyle}>
        Patient
      </Typography>

      {showPayer && (
        <Typography variant="caption" sx={headerStyle}>
          Payer
        </Typography>
      )}

      <Typography variant="caption" sx={headerStyle}>
        Next Action
      </Typography>

      <Typography variant="caption" sx={{ ...headerStyle, textAlign: 'right' }}>
        Amount
      </Typography>

      <Typography variant="caption" sx={headerStyle}>
        Status
      </Typography>

      {showIssues && (
        <Typography variant="caption" sx={headerStyle}>
          Issues
        </Typography>
      )}

      {/* Actions spacer */}
      <Box />
    </Box>
  );
};
